import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNavStack } from '@/hooks/useNavLayer';
import {
  adoptKeptCaptureForRuntime,
  listKeptCaptures,
  queueScopeStillCurrent,
  recordingWordsStatus,
  subscribeAudioQueueActivity,
  type KeptCaptureDestination,
  type KeptCaptureItem,
  type KeptCaptureListing,
  type RecordingWordsStatus,
} from '@/lib/audio-queue';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import {
  isCustodyAdmissionBusy,
  subscribeCustody,
  unsavedRecordingState,
} from '@/lib/voice-capture-custody';
import {
  isTestimonyDraftNoticeVisible,
  subscribeTopNoticeVisibility,
} from '@/lib/top-notice-visibility';
import { useNoticeSlot } from '@/lib/app-notice-stack';
import styles from './KeptRecordingNotice.module.css';

interface Acknowledgement {
  id: string;
  destination: KeptCaptureDestination;
  kind: 'adopted' | 'already-restored';
  fence: KeptCaptureListing['fence'];
}

const WHERE_KEY: Record<KeptCaptureDestination, string> = {
  home: 'keptRecording.whereHome',
  update: 'keptRecording.whereUpdate',
  testimony: 'keptRecording.whereTestimony',
  bookmark: 'keptRecording.whereBookmark',
};

const STATUS_KEY: Record<RecordingWordsStatus, string> = {
  ready: 'keptRecording.statusReady',
  processing: 'keptRecording.statusProcessing',
  waiting: 'keptRecording.statusWaiting',
  'check-later': 'keptRecording.statusCheckLater',
  failed: 'keptRecording.statusFailed',
  'voice-off': 'keptRecording.statusParked',
};

function custodyBlocks(): boolean {
  const unsaved = unsavedRecordingState();
  return isCustodyAdmissionBusy() || unsaved.failed > 0 || unsaved.unresolved > 0;
}

function focusableAndVisible(element: Element | null): element is HTMLElement {
  return element instanceof HTMLElement &&
    element.isConnected &&
    element.closest('[inert],[hidden]') === null &&
    element.getClientRects().length > 0;
}

/** Last-resort focus target: the page's main landmark, focusable only while it
 * holds focus (Layout keeps no permanent tabindex). */
function focusMainLandmark(): void {
  const main = document.getElementById('main-content');
  if (!main) return;
  const hadTabIndex = main.hasAttribute('tabindex');
  if (!hadTabIndex) {
    main.setAttribute('tabindex', '-1');
    main.addEventListener('blur', () => main.removeAttribute('tabindex'), { once: true });
  }
  main.focus({ preventScroll: true });
}

/**
 * App-level notice for kept recordings (account-change recording plan rev 4.1,
 * section 9; Part R proposal revision 2 and amendment 1). Lists this owner's
 * eligible kept recordings from IndexedDB, one at a time, newest first, and
 * adopts one only on an explicit Use. It never retargets, saves content or
 * deletes. Hidden while any overlay is open, while a recording is in progress
 * or unsaved, and while the testimony-draft notice shows, so it never covers a
 * dialog or competes with its focus. It never takes focus on appearance.
 */
export default function KeptRecordingNotice() {
  const { t } = useTranslation();
  // Presentation only: rendered (live region included) into the app notice
  // stack's kept slot. Every visibility rule below is unchanged.
  const slot = useNoticeSlot('kept');
  const nav = useNavStack();
  const location = useLocation();
  const overlayDepth = useSyncExternalStore(nav.subscribe, nav.length);
  const draftNoticeVisible = useSyncExternalStore(
    subscribeTopNoticeVisibility,
    isTestimonyDraftNoticeVisible,
  );
  const scope = useAudioQueueStore((state) => state.scope);
  const queueItems = useAudioQueueStore((state) => state.items);
  const [blockedByCapture, setBlockedByCapture] = useState(custodyBlocks);
  const [listing, setListing] = useState<KeptCaptureListing | null>(null);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set());
  const [ack, setAck] = useState<Acknowledgement | null>(null);
  const [failedId, setFailedId] = useState<string | null>(null);
  const [unavailableIds, setUnavailableIds] = useState<ReadonlySet<string>>(() => new Set());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [, setActivityTick] = useState(0);
  const [announcement, setAnnouncement] = useState('');

  const containerRef = useRef<HTMLElement | null>(null);
  const doneRef = useRef<HTMLButtonElement | null>(null);
  const primaryRef = useRef<HTMLButtonElement | null>(null);
  const focusOriginRef = useRef<Element | null>(null);
  const pendingFocusRef = useRef<'done' | 'next-use' | null>(null);
  const locationKeyRef = useRef(location.key);
  const busyRef = useRef(false);
  const listingRunRef = useRef<{ running: boolean; again: boolean }>({ running: false, again: false });

  useEffect(() => {
    locationKeyRef.current = location.key;
  }, [location.key]);

  const relist = useCallback(() => {
    const run = listingRunRef.current;
    if (run.running) {
      run.again = true;
      return;
    }
    run.running = true;
    void (async () => {
      try {
        do {
          run.again = false;
          let next: KeptCaptureListing | null = null;
          try {
            next = await listKeptCaptures();
          } catch {
            next = null;
          }
          setListing(next);
        } while (run.again);
      } finally {
        run.running = false;
      }
    })();
  }, []);

  // Discovery without a reload (revision 2 section 6.5): the scope opening or
  // changing, list changes, custody records leaving (a teardown commit may
  // have just written a kept row, which is never published to the list), the
  // page becoming visible (rows written by another tab), and each result.
  useEffect(() => {
    relist();
  }, [scope, queueItems, relist]);
  useEffect(() => subscribeCustody(() => {
    setBlockedByCapture(custodyBlocks());
    relist();
  }), [relist]);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') relist();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [relist]);
  // Start and finish of in-flight work, including failures that write nothing.
  useEffect(() => subscribeAudioQueueActivity(() => setActivityTick((n) => n + 1)), []);

  // An acknowledgement belongs to its scope only.
  const ackCurrent = ack !== null && queueScopeStillCurrent(ack.fence);
  const listingCurrent = listing !== null && queueScopeStillCurrent(listing.fence);
  const dismissKey = (item: KeptCaptureItem) =>
    `${listing?.fence.ownerId}:${listing?.fence.authEpoch}:${item.id}`;
  const visibleItems = listingCurrent
    ? listing.items.filter((item) => !dismissed.has(dismissKey(item)) && item.id !== ack?.id)
    : [];
  const item = visibleItems[0] ?? null;
  const hidden =
    overlayDepth > 0 ||
    blockedByCapture ||
    draftNoticeVisible ||
    (!ackCurrent && item === null);

  let statusLine = '';
  if (ackCurrent && ack) {
    const meta = queueItems.find((entry) => entry.id === ack.id);
    const status: RecordingWordsStatus = meta
      ? recordingWordsStatus(meta, ack.fence.ownerId, ack.fence.authEpoch)
      : 'waiting';
    const parts = [
      ack.kind === 'already-restored' ? t('keptRecording.alreadyRestored') : '',
      t(WHERE_KEY[ack.destination]),
      t(STATUS_KEY[status]),
      ack.destination === 'bookmark' &&
        (status === 'waiting' || status === 'check-later' || status === 'failed')
        ? t('keptRecording.bookmarkAction')
        : '',
    ];
    statusLine = parts.filter(Boolean).join(' ');
  }

  // Focus after a result, only when the notice still owns focus and nothing
  // intervened (amendment A3.3); otherwise the live region carries the outcome.
  useEffect(() => {
    if (hidden || !pendingFocusRef.current) return;
    const target = pendingFocusRef.current === 'done' ? doneRef.current : primaryRef.current;
    pendingFocusRef.current = null;
    target?.focus();
  });

  const announceVisibleAck = !hidden && ackCurrent ? statusLine : '';
  useEffect(() => {
    if (announceVisibleAck) setAnnouncement(announceVisibleAck);
  }, [announceVisibleAck]);

  // The first visible appearance of each kept recording is announced politely
  // once (never while hidden, never for a stale owner or epoch, never again on
  // re-listing), and focus is never moved for it.
  const announcedRef = useRef<Set<string>>(new Set());
  const unavailableNow = item !== null && (!item.available || unavailableIds.has(item.id));
  const appearanceKey = !hidden && !ackCurrent && item !== null && listingCurrent
    ? `${listing.fence.ownerId}:${listing.fence.authEpoch}:${item.id}`
    : null;
  const appearanceText = appearanceKey
    ? `${t('keptRecording.title')}. ${unavailableNow ? t('keptRecording.unavailable') : t('keptRecording.body')}`
    : '';
  useEffect(() => {
    if (!appearanceKey || announcedRef.current.has(appearanceKey)) return;
    // Identical text would not change the live region, so clear it first. The
    // key counts as announced only when the announcement is actually made.
    setAnnouncement('');
    const frame = requestAnimationFrame(() => {
      announcedRef.current.add(appearanceKey);
      setAnnouncement(appearanceText);
    });
    return () => cancelAnimationFrame(frame);
  }, [appearanceKey, appearanceText]);

  const focusInside = () =>
    Boolean(containerRef.current && containerRef.current.contains(document.activeElement));

  const leaveFocus = (moreRemain: boolean) => {
    if (!focusInside()) return;
    if (moreRemain) {
      pendingFocusRef.current = 'next-use';
      return;
    }
    const origin = focusOriginRef.current;
    if (focusableAndVisible(origin) && !containerRef.current?.contains(origin)) {
      origin.focus({ preventScroll: true });
    } else {
      focusMainLandmark();
    }
  };

  const handleUse = async (target: KeptCaptureItem) => {
    if (busyRef.current || !listing) return;
    busyRef.current = true;
    setBusyId(target.id);
    setFailedId(null);
    const fence = listing.fence;
    const tapLocationKey = locationKeyRef.current;
    let outcome: Awaited<ReturnType<typeof adoptKeptCaptureForRuntime>>;
    try {
      outcome = await adoptKeptCaptureForRuntime(target, fence);
    } catch {
      outcome = { status: 'refused', reason: 'error' };
    } finally {
      busyRef.current = false;
      setBusyId(null);
    }
    if (outcome.status === 'stale' || !queueScopeStillCurrent(fence)) {
      relist();
      return;
    }
    if (outcome.status === 'adopted' || outcome.status === 'already-restored') {
      const mayFocus =
        focusInside() && nav.length() === 0 && locationKeyRef.current === tapLocationKey;
      pendingFocusRef.current = mayFocus ? 'done' : null;
      setAck({ id: target.id, destination: target.destination, kind: outcome.status, fence });
    } else if (outcome.status === 'unavailable') {
      setUnavailableIds((current) => new Set(current).add(target.id));
    } else {
      setFailedId(target.id);
    }
    relist();
  };

  const handleNotNow = (target: KeptCaptureItem) => {
    const moreRemain = visibleItems.length > 1 || ackCurrent;
    leaveFocus(moreRemain);
    setDismissed((current) => new Set(current).add(dismissKey(target)));
  };

  const handleDone = () => {
    leaveFocus(visibleItems.length > 0);
    setAck(null);
  };

  const unavailable = unavailableNow;

  if (!slot) return null;

  return createPortal(
    <>
      <div className={styles.liveRegion} aria-live="polite" role="status">
        {announcement}
      </div>
      {!hidden && (
        <section
          ref={containerRef}
          className={styles.notice}
          aria-labelledby="kept-recording-title"
          onFocus={(event) => {
            const from = event.relatedTarget;
            if (from instanceof Element && !event.currentTarget.contains(from)) {
              focusOriginRef.current = from;
            }
          }}
        >
          {ackCurrent && ack ? (
            <>
              <p id="kept-recording-title" className={styles.message}>{statusLine}</p>
              <div className={styles.actions}>
                <button ref={doneRef} type="button" className={styles.primary} onClick={handleDone}>
                  {t('keptRecording.done')}
                </button>
              </div>
            </>
          ) : item ? (
            <>
              <h2 id="kept-recording-title" className={styles.title}>{t('keptRecording.title')}</h2>
              <p className={styles.message}>
                {unavailable ? t('keptRecording.unavailable') : t('keptRecording.body')}
              </p>
              {failedId === item.id && !unavailable && (
                <p className={styles.failed} role="alert">{t('keptRecording.useFailed')}</p>
              )}
              <div className={styles.actions}>
                {!unavailable && (
                  <button
                    ref={primaryRef}
                    type="button"
                    className={styles.primary}
                    // aria-disabled, not disabled: a focused button that becomes
                    // disabled loses focus, and the notice must still own focus
                    // when the result arrives. `busyRef` refuses a second tap.
                    aria-disabled={busyId !== null}
                    onClick={() => { void handleUse(item); }}
                  >
                    {t('keptRecording.use')}
                  </button>
                )}
                <button
                  type="button"
                  className={styles.secondary}
                  aria-disabled={busyId !== null}
                  onClick={() => { if (!busyRef.current) handleNotNow(item); }}
                >
                  {t('keptRecording.later')}
                </button>
              </div>
            </>
          ) : null}
        </section>
      )}
    </>,
    slot,
  );
}
